import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, ArrowRight, BookOpen } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Browse", path: "/browse" },
  { label: "Blog", path: "/blog" },
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => {
      setPosts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Blog | The Reading Room</title>
        <meta name="description" content="Curated reads, book lists, stories, and ideas from The Reading Room — updated every week." />
        <link rel="canonical" href="https://thereading-room.com/blog" />
      </Helmet>

      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: '#FAF7F2', borderColor: '#E8DFD0' }}>
        <div className="max-w-6xl mx-auto px-5 flex items-center justify-between h-14">
          <Link to="/" className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
            The Reading Room
          </Link>
          <div className="flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <Link key={l.path} to={l.path} className="text-sm font-medium transition-colors hover:opacity-70" style={{ color: 'var(--muted2)' }}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <main style={{ background: 'var(--bg-light)', minHeight: '100vh' }}>
        <div className="max-w-4xl mx-auto px-5 py-16">
          {/* Header */}
          <div className="mb-12">
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: 'var(--muted)' }}>
              From the Reading Room
            </p>
            <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
              Curated reads for curious minds
            </h1>
            <p className="text-base" style={{ color: 'var(--muted2)' }}>
              Hand-picked book lists, stories, and ideas — updated every week.
            </p>
          </div>

          {/* Posts */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-3 rounded-full animate-spin" style={{ borderColor: 'var(--highlight)', borderTopColor: 'transparent' }} />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20" style={{ color: 'var(--muted)' }}>
              <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p className="text-lg">No articles yet. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-0" data-testid="blog-post-list">
              {posts.map((post, index) => (
                <div key={post.slug}>
                  {index > 0 && (
                    <hr className="my-0" style={{ border: 'none', borderTop: '1px solid #E0D6C8' }} />
                  )}
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block overflow-hidden transition-all hover:opacity-90 py-8"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 md:gap-10`}>
                      <div className="md:w-[320px] md:min-w-[320px] overflow-hidden rounded-lg" style={{ aspectRatio: '4/3' }}>
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex flex-col justify-center flex-1">
                        <span
                          className="text-[10px] font-bold tracking-[0.15em] uppercase mb-3"
                          style={{ color: '#8B6E52', letterSpacing: '0.15em' }}
                        >
                          {post.category}
                        </span>
                        <h2 className="text-xl md:text-2xl font-bold leading-snug mb-3" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                          {post.title}
                        </h2>
                        <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted)' }}>
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted)' }}>
                            <Clock className="w-3.5 h-3.5" />
                            {post.read_time}
                          </span>
                          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#8B4513' }}>
                            Read the reflection <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-xs border-t" style={{ background: '#FAF7F2', borderColor: '#E8DFD0', color: 'var(--muted)' }}>
        <p>&copy; {new Date().getFullYear()} The Reading Room. All rights reserved.</p>
      </footer>
    </>
  );
}
